"use client";

import { useAbly } from "ably/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { notificationPromptSignalStore } from "@/app/providers/NotificationPermissionPrompt/notificationPromptSignalStore";
const DISMISSED_AT_KEY = "dc:notifications-dismissed-at";
const NEVER_ASK_KEY = "dc:notifications-never-ask";
function isDailyDismissActive(): boolean {
  try {
    const stored = localStorage.getItem(DISMISSED_AT_KEY);
    if (!stored) return false;
    const dismissedAt = Number(stored);
    if (Number.isNaN(dismissedAt)) return false;
    return Date.now() - dismissedAt < 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}
function isNeverAskSet(): boolean {
  try {
    return localStorage.getItem(NEVER_ASK_KEY) === "true";
  } catch {
    return false;
  }
}
function canShowNotificationPrompt(): boolean {
  return Notification.permission === "default" && !isNeverAskSet() && !isDailyDismissActive();
}
export const useNotificationPermission = () => {
  const client = useAbly();
  const [showModal, setShowModal] = useState(false);
  const [pushReady, setPushReady] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const activatingRef = useRef(false);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const activatePush = useCallback(async () => {
    if (activatingRef.current) return;
    activatingRef.current = true;
    if (mountedRef.current) setIsActivating(true);
    try {
      await client.push.activate();
      if (mountedRef.current && Notification.permission === "granted") {
        setPushReady(true);
      }
    } catch (err: unknown) {
      console.warn("Push activation failed", err);
    } finally {
      activatingRef.current = false;
      if (mountedRef.current) setIsActivating(false);
    }
  }, [client]);
  useEffect(() => {
    if (typeof window === "undefined" || !("Notification" in window)) return;
    const permission = Notification.permission;
    if (permission === "granted") {
      const handleGesture = () => void activatePush();
      document.addEventListener("click", handleGesture, {
        once: true
      });
      return () => document.removeEventListener("click", handleGesture);
    }
    if (permission === "denied") return;
    const unsubscribe = notificationPromptSignalStore.subscribe(() => {
      notificationPromptSignalStore.consumeUploadCompleteSignal();
      if (!mountedRef.current) return;
      if (!canShowNotificationPrompt()) return;
      setShowModal(true);
    });
    if (notificationPromptSignalStore.consumeUploadCompleteSignal()) {
      if (canShowNotificationPrompt()) {
        setShowModal(true);
      }
    }
    return () => unsubscribe();
  }, [activatePush]);
  const handleEnable = useCallback(async () => {
    localStorage.setItem(DISMISSED_AT_KEY, Date.now().toString());
    try {
      await activatePush();
    } catch (err_0: unknown) {
      console.warn("Notification permission request failed", err_0);
    } finally {
      if (mountedRef.current) {
        setShowModal(false);
      }
    }
  }, [activatePush]);
  const handleDismiss = useCallback(() => {
    localStorage.setItem(DISMISSED_AT_KEY, Date.now().toString());
    setShowModal(false);
  }, []);
  const handleNeverAsk = useCallback(() => {
    localStorage.setItem(NEVER_ASK_KEY, "true");
    setShowModal(false);
  }, []);
  return {
    showModal,
    pushReady,
    isActivating,
    handleEnable,
    handleDismiss,
    handleNeverAsk
  };
};